'use client';

// Global
import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  getWidgetData,
  SearchWidgetItem,
  WidgetRequestData,
  Context,
  ComparisonFilter,
} from '@sitecore-cloudsdk/search/browser';
import { tv } from 'tailwind-variants';

// Local
import { getTestProps } from 'lib/testing/utils';
import { useSearchInitialization } from 'lib/context/SearchInitializationContext';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { SearchLoading } from 'helpers/SearchLoading/SearchLoading';
import useDictionary from 'lib/hooks/useDictionary';
import { DICTIONARY_KEYS } from 'helpers/Constants/SiteConfig';
import {
  mapResultsToTrackEntities,
  trackSearchItemClick,
  trackSearchNavigationClick,
  trackSearchWidgetView,
} from 'lib/search/analytics';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';

type ReportsAndInsightsModel = {
  id: string;
  type?: string;
  title?: string;
  name?: string;
  url?: string;
  description?: string;
  date?: string;
  news_type?: string;
  image_url?: string;
  image_alt?: string;
  page_description?: string;
  source_id?: string;
};

type SearchResultsProps = {
  rfkId: string;
  sources: string[] | undefined;
};

type SearchResponse = {
  widgets: Array<{
    content: ReportsAndInsightsModel[];
    total_item?: number;
  }>;
};

const ReportsAndInsightsListSearchWidget = ({ rfkId, sources }: SearchResultsProps) => {
  const isEditing = useIsEditing();
  // Get Tailwind variant classes
  const { getDictionaryValue } = useDictionary();
  const { isSearchInitialized } = useSearchInitialization();
  const {
    container,
    header,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrow,
    heading,
    description,
    sectionDivider,
    footer,
    cta,
    imageWrapper,
    gridContainer,
    loadMoreContainer,
    loadingContainer,
    loadingText,
  } = TAILWIND_VARIANTS({ colCount: '2' });
  const [mobileFilterToogle] = useState<boolean>(false);
  const [reports, setReports] = useState<ReportsAndInsightsModel[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Ref to focus overlay when it opens
  const overlayRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  // Track if overlay should be focused after opening
  const [shouldFocusOverlay, setShouldFocusOverlay] = useState(false);

  // Focus overlay when it opens
  useEffect(() => {
    if (mobileFilterToogle && shouldFocusOverlay && overlayRef.current) {
      overlayRef.current.focus();
      setShouldFocusOverlay(false);
    }
  }, [mobileFilterToogle, shouldFocusOverlay]);

  const defaultLocale =
    process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE || process.env.DEFAULT_LANGUAGE || 'en';

  // Fetch data using Cloud SDK
  const fetchSearchData = useCallback(
    async (page: number = 1, isLoadMore: boolean = false) => {
      try {
        if (isLoadMore) {
          setIsLoadingMore(true);
        } else {
          setIsLoading(true);
        }

        // sources is string[]; use it directly, filtering empty/whitespace ones
        const searchSourceIds = Array.isArray(sources)
          ? sources.filter((s) => typeof s === 'string' && s.trim() !== '')
          : [];

        // Create context for the search
        const context = new Context({
          locale: {
            language: defaultLocale.split('-')[0] || 'en',
            country: defaultLocale.split('-')[1]?.toUpperCase() || 'US',
          },
        });

        // Build search widget configuration
        const typeFilter = new ComparisonFilter('type', 'eq', 'Press Release');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const widgetConfig: any = {
          limit: 4,
          content: {},
          offset: (page - 1) * 4,
          filter: typeFilter,
        };

        if (searchSourceIds.length > 0) {
          widgetConfig.sources = searchSourceIds;
        }

        const searchWidget = new SearchWidgetItem(
          'newsarticle',
          rfkId,
          widgetConfig,
          searchSourceIds.length > 0 ? (searchSourceIds as [string, ...string[]]) : undefined
        );

        // Fetch widget data
        const response = (await getWidgetData(
          new WidgetRequestData([searchWidget]),
          context
        )) as SearchResponse;

        if (response?.widgets?.[0]) {
          const widgetData = response.widgets[0];
          const nextContent = widgetData.content || [];

          if (isLoadMore) {
            setReports((prevReports) => [...prevReports, ...nextContent]);
          } else {
            setReports(nextContent);
          }
          setTotalItems(widgetData.total_item || 0);

          void trackSearchWidgetView({
            widgetId: rfkId,
            entities: mapResultsToTrackEntities(nextContent, 'newsarticle'),
            request: {
              numResults: nextContent.length,
              totalResults: widgetData.total_item || nextContent.length,
              pageNumber: page,
            },
          });
        }
      } catch (error) {
        console.error('Error fetching search data:', error);
        if (!isLoadMore) {
          setReports([]);
          setTotalItems(0);
        }
      } finally {
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    },
    [rfkId, sources, defaultLocale]
  );

  // Initial data fetch
  useEffect(() => {
    if (!isSearchInitialized) {
      return;
    }
    setCurrentPage(1);
    fetchSearchData(1, false);
  }, [fetchSearchData, isSearchInitialized]);

  // Load more function
  const handleLoadMore = useCallback(() => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchSearchData(nextPage, true);
    void trackSearchNavigationClick({
      widgetId: rfkId,
      itemPosition: nextPage,
    });
  }, [currentPage, fetchSearchData, rfkId]);

  // Memoized calculations for performance
  const hasMoreData = useMemo(() => {
    return reports.length < totalItems;
  }, [reports.length, totalItems]);

  const shouldShowLoadMore = useMemo(() => {
    return !isLoading && hasMoreData && reports.length > 0;
  }, [isLoading, hasMoreData, reports.length]);

  // Memoized reports rendering for performance
  const renderedReports = useMemo(() => {
    return reports.map((report, index) => {
      return (
        <article
          key={report.id || index}
          className={container()}
          data-component="authorable/shared/lists/carditem"
          {...editingHydrationProps(isEditing)}
          id={`report-${report.id || index}`}
          {...getTestProps(`card-item-${report.id || index}`)}
        >
          {/* Header/Image Section */}
          <div className={header()}>
            {report?.image_url ? (
              <ImageWrapper
                field={{
                  value: {
                    src: report?.image_url,
                    alt: report?.image_alt ? report?.image_alt : report?.name,
                  },
                }}
                className={imageWrapper()}
                priority
                {...getTestProps(`image`)}
              />
            ) : (
                <ImageWrapper
                  field={{
                    value: {
                      src: '/assets/profile_placeholder.png',
                      alt: 'No image',
                    },
                  }}
                  className={imageWrapper()}
                  {...getTestProps(`related-profile-image-placeholder`)}
                />
            )}
          </div>

          {/* Body/Content Section */}
          <div className={body()}>
            <div className={contentWrapper()}>
              {/* Content */}
              <div className={content()}>
                {/* Heading with Eyebrow */}
                <div className={headingWithEyebrow()}>
                  {report?.type && (
                    <div className={eyebrow()} {...getTestProps(`eyebrow`)}>
                      {report.type}
                    </div>
                  )}
                  {report?.name && (
                    <h3 className={heading()} {...getTestProps(`heading`)}>
                      {report.name}
                    </h3>
                  )}
                </div>

                {/* Description */}
                {report?.page_description && (
                  <div className={description()} {...getTestProps(`description`)}>
                    {report.page_description}
                  </div>
                )}

                {/* Section Divider */}
                {report?.url && <span className={sectionDivider()}></span>}
              </div>

              {/* Footer/CTA Section */}
              {report?.url && (
                <div className={footer()}>
                  <LinkWrapper
                    ctaComponentClass="component-card-item-button-2-color"
                    className={cta()}
                    ctaStyle={getCtaStyle({}, 'secondary')}
                    field={{
                      value: {
                        href: report.url,
                        text: getDictionaryValue(
                          DICTIONARY_KEYS.REPORTS_AND_INSIGHTS_LIST.READ_MORE
                        ),
                      },
                    }}
                    ctaIcon="arrow-right"
                    onClick={() => {
                      void trackSearchItemClick({
                        widgetId: rfkId,
                        entity: {
                          id: report.id,
                          entity: 'newsarticle',
                          uri: report.url,
                          sourceId: report.source_id,
                        },
                        itemPosition: index,
                      });
                    }}
                    {...getTestProps(`primary-cta`)}
                  />
                </div>
              )}
            </div>
          </div>
        </article>
      );
    });
  }, [
    reports,
    container,
    header,
    imageWrapper,
    body,
    contentWrapper,
    content,
    headingWithEyebrow,
    eyebrow,
    heading,
    description,
    sectionDivider,
    footer,
    cta,
    getDictionaryValue,
    rfkId,
  ]);

  return (
    <div
      ref={widgetRef}
      id="reports-and-insights-list-cards"
      {...getTestProps(`reports-and-insights-list-cards`)}
    >
      {isLoading ? (
        <SearchLoading isLoading={isLoading} viewBox="0 0 20 20" />
      ) : (
        <>
          {(totalItems > 0 || reports.length > 0) && (
            <>
              <div className={gridContainer()}>{renderedReports}</div>

              {/* Load More Button */}
              {shouldShowLoadMore && (
                <div className={loadMoreContainer()}>
                  {isLoadingMore ? (
                    <div className={loadingContainer()} {...getTestProps(`load-more-loading`)}>
                      <SearchLoading isLoading={true} size="s" viewBox="0 0 20 20" />
                      <span className={loadingText()}>
                        {getDictionaryValue(DICTIONARY_KEYS.REPORTS_AND_INSIGHTS_LIST.LOADING)}
                      </span>
                    </div>
                  ) : (
                    <ButtonWrapper
                      onClick={handleLoadMore}
                      ctaComponentClass="default"
                      ctaStyle={getCtaStyle({}, 'primary')}
                      remixIcon="add-fill"
                      {...getTestProps(`load-more-button`)}
                      className="!w-auto"
                    >
                      {getDictionaryValue(DICTIONARY_KEYS.REPORTS_AND_INSIGHTS_LIST.LOAD_MORE)}
                    </ButtonWrapper>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsAndInsightsListSearchWidget;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    colCount: '2',
  },
  slots: {
    container: [
      'flex',
      'w-full',
      'h-full',
      'overflow-hidden',
      'rounded-general-border-radius-card',
      'bg-component-card-item-surface',
      'shadow-color-black-alpha-10',
      'shadow-[0_0_8px_0]',
    ],
    header: [
      'relative',
      'min-h-max',
      'w-full',
      'h-full',
      'lg:h-auto',
      'overflow-hidden',
      'aspect-[1.61/1]',
      'text-color-fill-brand-1',
      'transition',
      'duration-300',
      'ease-in-out',
      'group',
      'pt-component-article-card-portrait-image-padding-top',
      'px-component-article-card-portrait-image-padding-x',
      'pb-component-article-card-portrait-image-padding-bottom',
      'flex-auto md:flex-1 lg:flex-none',
    ],
    body: [
      'flex',
      'flex-row',
      'items-start',
      'text-left',
      'h-auto',
      'lg:h-full',
      'lg:justify-between',
      'flex-1',
    ],
    contentWrapper: [
      'flex',
      'flex-col',
      'w-full',
      'gap-spacing-spacing-24',
      'text-left',
      'h-auto',
      'lg:h-full',
      'justify-start',
      'lg:justify-between',
      'py-component-card-item-landscape-content-padding-y',
      'px-component-card-item-landscape-content-padding-x',
    ],
    content: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-24', 'text-left'],
    headingWithEyebrow: ['flex', 'flex-col', 'w-full', 'gap-spacing-spacing-4'],
    eyebrow: [
      'text-component-card-item-eyebrow',
      'font-typography-font-family-body',
      'text-typography-font-size-body-xSmall',
      'mb-spacing-spacing-4',
      'font-semibold',
      'leading-[150%]',
    ],
    heading: [
      'font-typography-font-family-header',
      'font-normal',
      'text-typography-font-size-medium',
      'text-component-card-item-title',
      'leading-typography-line-height-medium',
    ],
    description: [
      'font-typography-font-family-body',
      'font-typography-font-weight-body',
      'text-typography-font-size-body-medium',
      'text-component-card-item-body',
      'leading-typography-line-height-body-medium',
      'mb-spacing-spacing-8',
    ],
    sectionDivider: [
      'w-component-section-divider-width',
      'h-component-section-divider-height',
      'bg-component-section-divider',
    ],
    footer: [
      'flex',
      'flex-wrap',
      'flex-col',
      'gap-spacing-spacing-8',
      'items-start',
      'justify-center',
      'w-full',
      'mt-auto',
    ],
    cta: [
      'flex',
      'gap-spacing-spacing-8',
      'items-center',
      'justify-center',
      'py-spacing-spacing-16',
      'px-spacing-spacing-24',
      '!w-auto',
    ],
    imageWrapper: [
      'w-full',
      'h-full',
      'aspect-[1.61/1]',
      'lg:aspect-[628.00/353.52]',
      'object-cover',
    ],
    // Grid container for the list
    gridContainer: [
      'grid',
      'grid-cols-1',
      'lg:grid-cols-2',
      'gap-y-space-between-base',
      'gap-x-layout-base-gutter',
    ],
    // Load more button container
    loadMoreContainer: ['flex', 'justify-center', 'mt-component-section-container-gutter'],
    // Loading state container
    loadingContainer: ['flex', 'items-center', 'gap-spacing-spacing-8'],
    // Loading text
    loadingText: [
      'text-component-button-primary-text',
      'font-typography-font-family-body',
      'font-semibold',
    ],
  },
  variants: {
    colCount: {
      '2': {
        container: ['flex-col', 'md:flex-row', 'lg:flex-col'],
        header: [
          'aspect-[1.61/1]',
          'lg:aspect-[628.00/353.52]',
          'sm:py-component-article-card-landscape-image-padding-y',
          'sm:px-component-article-card-landscape-image-padding-x',
          'sm:pr-0',
          'rounded-r-none',
          'lg:rounded-r-none',
        ],
        imageWrapper: ['rounded-r-none', 'lg:rounded-r-none'],
      },
    },
  },
});
